import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toggleModal, modalState, getLoadingState } from 'reducers/ui';
import { connect, useSelector, useDispatch } from 'react-redux';

import { filters } from './options';
import staticText from 'static/text_display.json';
import useBusinessClient from 'containers/Pages/People/BusinessClients/Layouts/useBusinessClient';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import useFilters from 'containers/Base/useFilters';
import { PAGE_NAME } from 'utils/constants';
import {
  TASK_STATUS_FILTERS,
  REPORTING_TASK_STATUS_FILTERS,
} from 'utils/constants/task';
import { getSecurityId, getUserAssignedDepartmentList } from 'reducers/users';
import LayoutWithMap from 'containers/Base/LayoutWithMap';

import { actions as dataGridActions } from '../Modules/dataGridTable';
import {
  actions as filterActions,
  selectors as filterSelectors,
} from '../Modules/filters';
import {
  getGridListTransformed,
  getTotalRowsCount,
  getForceRefresh,
  getSelectedRowID,
} from '../Selectors/cleaningTaskSelectors';
import { getSecurityCompanyServices } from 'reducers/securityCompany';

const CleanerTask = ({
  cols,
  downloadable,
  currentPage,
  modalOpen,
  toggleModal,
  cleaningTasks,
  setCleaningTaskFilterKey,
  editable,
  filterValues,
  setFilters,
  fetchCleaningTaskQr,
  fetchCleaningTaskList,
  totalEntries,
  forceRefresh,
  setRefreshCleaningTasks,
  FormComponent,
  DetailComponent,
  fetchCleaningTaskListPdf,
  defaultFilterValues,
  customFetchTaskList,
  operationComponent,
  isTemplate,
  map,
  companyId,
  userAssignedDepartments = [],
  persistentStateName,
  services,
  loading,
  forceOpenDetailPanel,
}) => {
  const fetchTaskList = customFetchTaskList || fetchCleaningTaskList;
  const bizClients = useBusinessClient();
  const sites = useSites();
  const [cleanerGridAPI, setCleanerGridAPI] = useState();
  const { localFilterValues, updateFilter } = useFilters(
    !isTemplate
      ? {
          filterValues: {
            ...filterValues,
            status: defaultFilterValues.status,
            taskCategory: defaultFilterValues.taskCategory,
          },
        }
      : { filterValues: { ...defaultFilterValues, ...filterValues } },
  );
  // actions--------------------------------------------
  const dispatch = useDispatch();

  const resetCleanerDetails = useCallback(() => {
    dispatch(dataGridActions.setGridRowDetails({ details: {} }));
  }, [dispatch]);

  const removeSelectedMapCleanerTaskId = useCallback(() => {
    dispatch(dataGridActions.setSelectedRowID(null));
  }, [dispatch]);
  //-------------------------------------------------------
  const gridAPIHandler = gridAPI => {
    setCleanerGridAPI(gridAPI);
  };

  const onMapSelectedCleanerTaskId = useSelector(state =>
    getSelectedRowID(state),
  );

  const cleanerTaskHighlight = onMapSelectedCleanerTaskId => {
    if (onMapSelectedCleanerTaskId != null) {
      const node =
        cleanerGridAPI && cleanerGridAPI.getRowNode(onMapSelectedCleanerTaskId);
      // Go to the highlighted node
      cleanerGridAPI &&
        cleanerGridAPI.ensureNodeVisible(node, { position: 'top' });
      if (node) {
        if (!node.isSelected()) {
          node.setSelected(true);
        }
      }
    }
  };

  const cancelDetailPanel = () => {
    // remove the map selected id from the state
    resetCleanerDetails();
    removeSelectedMapCleanerTaskId();
  };

  const onSelectedCleanerTask = selectedData => {
    if (
      map &&
      selectedData &&
      selectedData.surface?.lat &&
      selectedData.surface?.lng &&
      !selectedData.surfaceId
    ) {
      map.panTo({
        lat: selectedData.surface?.lat,
        lng: selectedData.surface?.lng,
      });
    }
  };

  cleanerTaskHighlight(onMapSelectedCleanerTaskId);

  const onUpdate = filters => {
    fetchTaskList({ location: currentPage, filters });
  };

  const onDownloadClick = () => {
    fetchCleaningTaskListPdf(localFilterValues);
  };

  const fetchCleanerTask = from => {
    fetchTaskList({
      filters: localFilterValues,
      location: currentPage,
      from,
    });
    //  we have default values for filters, it needs to be insync with the redux state as well
    setFilters(localFilterValues);
  };

  const getListData = cleaningTasks => {
    return cleaningTasks.map(p => ({
      ...p,
      btnData: { id: p.id, onClick: id => fetchCleaningTaskQr({ id }) },
    }));
  };

  return (
    <LayoutWithMap
      header="Tasks"
      cols={cols}
      loading={loading}
      downloadable={downloadable}
      {...(downloadable && {
        onDownloadClick: onDownloadClick,
        downloadHoverText: staticText.reporting_view.task_download,
      })}
      data={getListData(cleaningTasks)}
      DetailComponent={DetailComponent}
      operationComponent={operationComponent}
      // for creation / edit
      toggleModal={toggleModal}
      onEdit={modalOpen}
      filtersRequired={true}
      filters={filters({
        currentView: currentPage,
        filterValues: localFilterValues,
        bizClients,
        sites,
        businessClient: localFilterValues.businessClient,
        cleaningStatusFilters:
          currentPage === 'reporting'
            ? REPORTING_TASK_STATUS_FILTERS
            : TASK_STATUS_FILTERS,
        taskTypeFilters: defaultFilterValues.taskType,
        companyId,
        userAssignedDepartments,
        services,
      })}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: vals => {
            setCleaningTaskFilterKey(true);
            setFilters(vals);
          },
        })
      }
      filterValues={localFilterValues}
      onUpdate={onUpdate}
      editable={editable}
      FormComponent={FormComponent}
      fetchData={fetchCleanerTask}
      rowModelType="infinite"
      forceRefresh={forceRefresh}
      setForceRefresh={setRefreshCleaningTasks}
      totalEntries={totalEntries}
      enhancedCancellation={cancelDetailPanel}
      setGridAPI={gridAPIHandler}
      enhancedOnSelect={onSelectedCleanerTask}
      createTextHover={staticText.cleaner_operational_view.task.create}
      defaultFilterValues={defaultFilterValues}
      persistentStateName={persistentStateName}
      forceOpenDetailPanel={forceOpenDetailPanel}
    />
  );
};

CleanerTask.propTypes = {
  onEdit: PropTypes.bool,
  downloadable: PropTypes.bool,
  toggleModal: PropTypes.func,
  editable: PropTypes.bool,
  CleaningTasks: PropTypes.array,
  filters: PropTypes.array,
  modalOpen: PropTypes.bool,
  setFilters: PropTypes.func,
  filterValues: PropTypes.object,
  filtersRequired: PropTypes.bool,
  DetailComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  currentPage: PropTypes.string,
  persistentStateName: PropTypes.string,
  currentView: PropTypes.string,
  companyId: PropTypes.string,
  forceOpenDetailPanel: PropTypes.bool,
  fetchCleaningTaskList: PropTypes.func,
  totalEntries: PropTypes.number,
  forceRefresh: PropTypes.bool,
  setRefreshCleaningTasks: PropTypes.func,
  FormComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  enhancedCancellation: PropTypes.func,
  setGridAPI: PropTypes.func,
  enhancedOnSelect: PropTypes.func,
  fetchCleaningTaskQr: PropTypes.func,
  secondaryColumn: PropTypes.object,
  cols: PropTypes.array,
  services: PropTypes.array,
  cleaningTasks: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  fetchCleaningTaskListPdf: PropTypes.func,
  defaultFilterValues: PropTypes.object,
  isTemplate: PropTypes.bool,
  loading: PropTypes.bool,
  customFetchTaskList: PropTypes.func,
  operationComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  resetCleanerDetail: PropTypes.func,
  setCleaningTaskFilterKey: PropTypes.func,
  map: PropTypes.object,
};

const mapStateToProps = state => ({
  cleaningTasks: getGridListTransformed(state),
  filterValues: filterSelectors.getFilterValues(state),
  totalEntries: getTotalRowsCount(state),
  forceRefresh: getForceRefresh(state),
  companyId: getSecurityId(state),
  userAssignedDepartments: getUserAssignedDepartmentList(state),
  modalOpen: modalState(state)[PAGE_NAME.CLEANING_TASKS],
  services: getSecurityCompanyServices(state),
  loading: getLoadingState(state),
});

const mapDispatchToProps = dispatch => ({
  toggleModal: () => dispatch(toggleModal(PAGE_NAME.CLEANING_TASKS)),
  setFilters: filters => dispatch(filterActions.setFilters(filters)),
  fetchCleaningTaskList: ({ filters, from }) =>
    dispatch(dataGridActions.fetchGridList({ filters, from })),
  setRefreshCleaningTasks: (val, location) =>
    dispatch(dataGridActions.setForceRefresh(val, location)),
  fetchCleaningTaskListPdf: filters =>
    dispatch(dataGridActions.fetchGridListPdf({ filters })),
  fetchCleaningTaskQr: ({ id }) => dispatch(dataGridActions.fetchQr({ id })),
  setCleaningTaskFilterKey: (location, key) =>
    dispatch(filterActions.setFilterKey(location, key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CleanerTask);
